import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/formatters';
import type {
  DrawdownMatrixCell,
  DrawdownMatrixColumn,
  DrawdownMatrixRow,
} from '@/types/drawdown';
import { formatMatrixAmount } from './drawdownMatrixAmount';
import { getCellClass, getStatusLabel } from './drawdownMatrixStatus';
import { totalColumn, totalRows } from './drawdownMatrixTotals';

const STICKY_INVESTOR_COLUMN =
  'sticky left-0 z-20 bg-background border-r text-left min-w-[240px]';

interface DrawdownMatrixTableProps {
  columns: DrawdownMatrixColumn[];
  /** The rows on screen: the totals row follows the filters, not the fund */
  rows: DrawdownMatrixRow[];
  currency: string;
  onSelectDrawdown: (drawdownId: string) => void;
  onSelectInvestor: (investorId: string) => void;
}

function cellTitle(cell: DrawdownMatrixCell, currency: string): string {
  const parts = [
    getStatusLabel(cell.status),
    `due ${formatDate(cell.due_date)}`,
    `${formatMatrixAmount(cell.amount_paid, currency)} received`,
  ];
  if (cell.days_overdue > 0) parts.push(`${cell.days_overdue} days overdue`);
  return parts.join(' · ');
}

function CellNote({
  cell,
  currency,
}: {
  cell: DrawdownMatrixCell;
  currency: string;
}) {
  if (cell.status === 'exempted') {
    return <span className="block text-[10px] leading-tight">exempt</span>;
  }
  if (
    cell.status === 'partial' ||
    (cell.status === 'draft' && cell.amount_paid > 0)
  ) {
    return (
      <span className="block text-[10px] leading-tight">
        {formatMatrixAmount(cell.amount_paid, currency)} received
      </span>
    );
  }
  if (cell.days_overdue > 0) {
    return (
      <span className="block text-[10px] leading-tight">
        {cell.days_overdue}d overdue
      </span>
    );
  }
  return null;
}

function MatrixCell({
  cell,
  currency,
}: {
  cell: DrawdownMatrixCell | undefined;
  currency: string;
}) {
  if (!cell) {
    return (
      <td
        className="px-3 py-2 text-right text-muted-foreground/50 tabular-nums"
        title="Not included in this call"
      >
        —
      </td>
    );
  }

  return (
    <td
      className={`px-3 py-2 text-right tabular-nums ${getCellClass(cell.status)}`}
      title={cellTitle(cell, currency)}
    >
      <span className={cell.status === 'exempted' ? 'line-through' : undefined}>
        {formatMatrixAmount(cell.amount_due, currency)}
      </span>
      <CellNote cell={cell} currency={currency} />
    </td>
  );
}

function ColumnHeader({
  column,
  onSelect,
}: {
  column: DrawdownMatrixColumn;
  onSelect: () => void;
}) {
  return (
    <th className="px-3 py-2 text-right align-bottom font-medium min-w-[130px]">
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full flex-col items-end gap-0.5 hover:text-primary focus-visible:outline-2"
      >
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          {column.status === 'draft' && (
            <Badge
              variant="outline"
              className="px-1.5 py-0 text-[10px] font-normal"
            >
              Draft
            </Badge>
          )}
          DD-{column.drawdown_number}
        </span>
        <span className="text-[11px] font-normal text-muted-foreground truncate max-w-[150px]">
          {column.purpose}
        </span>
        <span
          className={`text-[11px] font-normal ${
            column.days_overdue > 0
              ? 'text-red-600 dark:text-red-400'
              : 'text-muted-foreground'
          }`}
        >
          Due {formatDate(column.due_date)}
        </span>
      </button>
    </th>
  );
}

export function DrawdownMatrixTable({
  columns,
  rows,
  currency,
  onSelectDrawdown,
  onSelectInvestor,
}: DrawdownMatrixTableProps) {
  const visibleTotals = totalRows(rows);

  return (
    <div className="relative max-h-[70vh] w-full overflow-auto rounded-md border">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr className="[&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:border-b [&>th]:bg-muted/60">
            <th
              className={`${STICKY_INVESTOR_COLUMN} z-30 px-4 py-2 font-medium`}
            >
              Investor
            </th>
            {columns.map((column) => (
              <ColumnHeader
                key={column.drawdown_id}
                column={column}
                onSelect={() => onSelectDrawdown(column.drawdown_id)}
              />
            ))}
            <th className="border-l px-3 py-2 text-right align-bottom font-medium">
              Called
            </th>
            <th className="px-3 py-2 text-right align-bottom font-medium">
              Paid
            </th>
            <th className="px-3 py-2 text-right align-bottom font-medium">
              Outstanding
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.investor_id} className="border-b hover:bg-muted/40">
              <th
                scope="row"
                className={`${STICKY_INVESTOR_COLUMN} px-4 py-2 font-normal`}
              >
                <button
                  type="button"
                  onClick={() => onSelectInvestor(row.investor_id)}
                  className="text-left hover:text-primary focus-visible:outline-2"
                >
                  <span className="block font-medium">{row.investor_name}</span>
                  <span className="block text-[11px] text-muted-foreground tabular-nums">
                    {formatMatrixAmount(row.commitment_amount, currency)}{' '}
                    committed
                  </span>
                </button>
              </th>

              {columns.map((column) => (
                <MatrixCell
                  key={column.drawdown_id}
                  cell={row.cells[column.drawdown_id]}
                  currency={currency}
                />
              ))}

              <td className="border-l px-3 py-2 text-right tabular-nums">
                {formatMatrixAmount(row.amount_due, currency)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatMatrixAmount(row.amount_paid, currency)}
              </td>
              <td
                className={`px-3 py-2 text-right tabular-nums ${
                  row.outstanding > 0
                    ? 'font-semibold text-red-700 dark:text-red-400'
                    : 'text-muted-foreground'
                }`}
              >
                {row.outstanding > 0
                  ? formatMatrixAmount(row.outstanding, currency)
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr className="[&>td]:sticky [&>td]:bottom-0 [&>td]:z-10 [&>td]:border-t [&>td]:bg-muted/60 [&>td]:font-semibold">
            <td className={`${STICKY_INVESTOR_COLUMN} z-30 px-4 py-2`}>
              Total · {rows.length}{' '}
              {rows.length === 1 ? 'investor' : 'investors'}
            </td>
            {columns.map((column) => {
              const columnTotals = totalColumn(rows, column.drawdown_id);
              return (
                <td
                  key={column.drawdown_id}
                  className="px-3 py-2 text-right tabular-nums"
                >
                  {formatMatrixAmount(columnTotals.amountDue, currency)}
                  <span className="block text-[11px] font-normal text-muted-foreground">
                    {columnTotals.amountDue > 0
                      ? Math.round(
                          (columnTotals.amountPaid / columnTotals.amountDue) *
                            100
                        )
                      : 0}
                    % paid
                  </span>
                </td>
              );
            })}
            <td className="border-l px-3 py-2 text-right tabular-nums">
              {formatMatrixAmount(visibleTotals.amountDue, currency)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {formatMatrixAmount(visibleTotals.amountPaid, currency)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {formatMatrixAmount(visibleTotals.outstanding, currency)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
